/**
 * Adapted from PlayCanvas Engine's gsplat-quad-renderer.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { syncGSplatMaterialTemplate } from '@/plugins/gsplat/effects/GSplatMaterialTemplateSync'
import type {
  GSplatBufferLeaf,
  GSplatDrawObjectLeaf,
  GSplatMaterialBlendMode,
  GSplatMaterialLeaf,
  GSplatMaterialSnapshot,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import {
  GSPLAT_RENDER_MODE_FORWARD,
  GSPLAT_RENDER_MODE_NONE,
  GSPLAT_RENDER_MODE_SHADOW,
  type GSplatRenderPassMask,
  hasGSplatRenderPass,
} from './GSplatPipelineTypes'
import {
  GSPLAT_INSTANCE_SIZE,
  GSplatRenderer,
  type GSplatRendererOptions,
  type GSplatRenderViewParams,
} from './GSplatRenderer'
import gsplatRasterWorkBufferVS from './shaders/gsplatRasterWorkBuffer.glsl'

export interface GSplatQuadRendererOptions extends GSplatRendererOptions {
  readonly material: GSplatMaterialLeaf
  readonly drawObject: GSplatDrawObjectLeaf
}

/** CPU global order 与 GPU quad raster 的 PlayCanvas 对齐消费者。 */
export class GSplatQuadRenderer extends GSplatRenderer {
  readonly meshInstance: GSplatDrawObjectLeaf
  private readonly _material: GSplatMaterialLeaf
  private originalBlendMode: GSplatMaterialBlendMode = 'additive'
  private readonly _internalDefines = new Set<string>()
  private readonly _boundStreamNames = new Set<string>()
  private _lastFisheyeEnabled = false
  private _lastNoFog: boolean | undefined
  private _lastSourceChunksKey = ''
  private _sourceMaterialVersion = -1
  private _sourceTemplateRevision = -1
  private readonly _viewport = new THREE.Vector2()
  private readonly _viewportSize = new THREE.Vector4()
  private readonly _cameraParams = new THREE.Vector4()
  private readonly _cameraWorldPosition = new THREE.Vector3()
  private readonly _cameraPositionHigh = new THREE.Vector3()
  private readonly _cameraPositionLow = new THREE.Vector3()
  private readonly _originToCameraHigh = new THREE.Vector3()
  private readonly _originToCameraLow = new THREE.Vector3()
  private readonly _clipWorldOffset = new THREE.Vector3()

  constructor(options: GSplatQuadRendererOptions) {
    super(options)
    this._material = options.material
    this.meshInstance = options.drawObject
    this._configureMaterial()
    for (const [name] of this._material.captureSnapshot(this.adapter.language).defines) {
      this._internalDefines.add(name)
    }
    for (const name of [
      '{GSPLAT_INSTANCE_SIZE}',
      'GSPLAT_UNIFIED_ID',
      'PICK_CUSTOM_ID',
      'GSPLAT_INDIRECT_DRAW',
      'GSPLAT_SEPARATE_OPACITY',
      'GSPLAT_WORKBUFFER_DATA',
      'GSPLAT_SHADER_EFFECT_VIEW_GUARD',
      'GSPLAT_FISHEYE',
      'GSPLAT_NO_FOG',
      'GSPLAT_OVERDRAW',
    ]) {
      this._internalDefines.add(name)
    }
    this.meshInstance.visible = false
    this.meshInstance.instancingCount = 0
    this.meshInstance.pickable = false
    this.meshInstance.setVisibilityFilter((camera) => {
      const renderMode = this.renderMode ?? GSPLAT_RENDER_MODE_NONE
      if (
        camera === this.cameraNode &&
        hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_FORWARD)
      ) {
        return true
      }
      return (
        hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_SHADOW) &&
        camera.name === 'pcShadowCamera'
      )
    })
  }

  override get material(): GSplatMaterialLeaf {
    return this._material
  }

  override setRenderMode(renderMode: GSplatRenderPassMask): void {
    this.assertActive()
    const oldRenderMode = this.renderMode ?? GSPLAT_RENDER_MODE_NONE
    const wasForward = hasGSplatRenderPass(oldRenderMode, GSPLAT_RENDER_MODE_FORWARD)
    const wasShadow = hasGSplatRenderPass(oldRenderMode, GSPLAT_RENDER_MODE_SHADOW)
    const isForward = hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_FORWARD)
    const isShadow = hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_SHADOW)

    this.meshInstance.castShadow = isShadow
    if (wasForward && !isForward) this.layer.removeMeshInstances([this.meshInstance], true)
    if (wasShadow && !isShadow) this.layer.removeShadowCasters([this.meshInstance])
    if (!wasForward && isForward) this.layer.addMeshInstances([this.meshInstance], true)
    if (!wasShadow && isShadow) this.layer.addShadowCasters([this.meshInstance])
    super.setRenderMode(renderMode)
  }

  override destroy(): void {
    if (this.destroyed) return
    const renderMode = this.renderMode ?? GSPLAT_RENDER_MODE_NONE
    if (hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_FORWARD)) {
      this.layer.removeMeshInstances([this.meshInstance], true)
    }
    if (hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_SHADOW)) {
      this.layer.removeShadowCasters([this.meshInstance])
    }
    this._material.dispose()
    this.meshInstance.dispose()
    super.destroy()
  }

  override onWorkBufferFormatChanged(): void {
    this.workBuffer.syncWithFormat()
    this._configureMaterial()
  }

  override update(count: number, textureSize: number): void {
    this.assertActive()
    if (!Number.isSafeInteger(count) || count < 0) {
      throw new Error(`非法 GSplat visible count: ${count}`)
    }
    // PlayCanvas 的 GSplatStreams.resize() 会原地 resize Texture，因此材质绑定保持有效。
    // 本引擎的 adapter leaf 采用 replacement storage：WorkBuffer resize transaction 会替换
    // 全部纹理对象。Manager 在 rebuilt 后调用 update()，这里必须把 raster material 重绑到
    // 新 storage，否则顶点 shader 会继续采样已经销毁的初始纹理。
    this._bindWorkBufferTextures()
    this.meshInstance.instancingCount = Math.ceil(count / GSPLAT_INSTANCE_SIZE)
    this._material.setParameter('numSplats', count)
    this._material.setParameter('splatTextureSize', textureSize)
    this._material.setParameter('dataWidth', textureSize)
    this._material.setParameter('orderWidth', textureSize)
    this.meshInstance.visible = count > 0
  }

  override refreshDataSourceBindings(): void {
    this.assertActive()
    this._bindWorkBufferTextures()
    this.setOrderData()
  }

  override setGpuSortedRendering(
    drawSlot: number,
    sortedIds: GSplatBufferLeaf,
    numSplatsBuffer: GSplatBufferLeaf,
    textureSize: number
  ): void {
    this.assertActive()
    this.meshInstance.setIndirect(drawSlot)
    this._material.setParameter('compactedSplatIds', sortedIds)
    this._material.setParameter('numSplatsStorage', numSplatsBuffer)
    this._material.setParameter('splatTextureSize', textureSize)
    if (this._material.getDefine('GSPLAT_INDIRECT_DRAW') !== true) {
      this._material.setDefine('GSPLAT_INDIRECT_DRAW', true)
      this._material.update()
    }
    this.meshInstance.visible = true
    if (this.meshInstance.instancingCount <= 0) this.meshInstance.instancingCount = 1
  }

  override setCpuSortedRendering(): void {
    this.assertActive()
    this.meshInstance.setIndirect(-1)
    if (this._material.getDefine('GSPLAT_INDIRECT_DRAW')) {
      this._material.setDefine('GSPLAT_INDIRECT_DRAW', false)
      this._material.update()
    }
    this.setOrderData()
    this.meshInstance.visible = false
  }

  override setOrderData(): void {
    this.assertActive()
    const order = this.workBuffer.orderResource
    if (order) this._material.setParameter('splatOrder', order)
    else this._material.deleteParameter('splatOrder')
  }

  override frameUpdate(params: GSplatRenderViewParams, exposure = 1): void {
    this.assertActive()
    if (params.colorRamp) {
      this._material.setParameter('colorRampIntensity', params.colorRampIntensity)
    }

    const camera = params.cameraNode as THREE.Camera & { readonly fov?: number }
    if (!camera.projectionMatrix) {
      throw new Error('GSplatQuadRenderer frameUpdate 要求 cameraNode 提供 projectionMatrix')
    }
    this.fisheyeProj.update(
      this.resolveFisheye(params.fisheye),
      camera.fov ?? 0,
      camera.projectionMatrix
    )
    const fisheyeEnabled = this.fisheyeProj.enabled
    if (fisheyeEnabled !== this._lastFisheyeEnabled) {
      this._lastFisheyeEnabled = fisheyeEnabled
      this._material.setDefine('GSPLAT_FISHEYE', fisheyeEnabled)
      this._material.update()
    }
    if (fisheyeEnabled) {
      this._material.setParameter('fisheye_k', this.fisheyeProj.k)
      this._material.setParameter('fisheye_inv_k', this.fisheyeProj.invK)
      this._material.setParameter('fisheye_projMat00', this.fisheyeProj.projMat00)
      this._material.setParameter('fisheye_projMat11', this.fisheyeProj.projMat11)
    }

    const noFog = !params.useFog
    if (noFog !== this._lastNoFog) {
      this._lastNoFog = noFog
      this._material.setDefine('GSPLAT_NO_FOG', noFog)
      this._material.update()
    }

    this._syncWithWorkBufferFormat()
    if (params.materialTemplate) {
      if (this._sourceTemplateRevision !== params.materialTemplate.revision) {
        syncGSplatMaterialTemplate(this._material, params.materialTemplate)
        this._sourceTemplateRevision = params.materialTemplate.revision
      }
    } else if (this._sourceMaterialVersion !== params.material.revision) {
      this._copyMaterialSettings(params.material)
      this._sourceMaterialVersion = params.material.revision
    }
    this._material.setParameter('gsplatShaderEffectEnabled', params.shaderEffectEnabled === true)
    if (params.sceneOrigin) this._clipWorldOffset.copy(params.sceneOrigin)
    else this._clipWorldOffset.set(0, 0, 0)
    this._material.setParameter('gsplatClipWorldOffset', this._clipWorldOffset)
    this._updateFrameUniforms(params, camera, exposure)
  }

  override updateOverdrawMode(params: GSplatRenderViewParams): void {
    this.assertActive()
    const enabled = Boolean(params.colorRamp)
    const wasEnabled = Boolean(this._material.getDefine('GSPLAT_OVERDRAW'))
    if (enabled) {
      this._material.setParameter('colorRamp', params.colorRamp)
      this._material.setParameter('colorRampIntensity', params.colorRampIntensity)
    }
    if (enabled === wasEnabled) return

    this._material.setDefine('GSPLAT_OVERDRAW', enabled)
    if (enabled) {
      this.originalBlendMode = this._material.getRasterState().blendMode
      this._material.setRasterState({ blendMode: 'additive' })
    } else {
      this._material.setRasterState({ blendMode: this.originalBlendMode })
    }
    this._material.update()
  }

  protected override setRasterLeafBounds(aabb: THREE.Box3 | null): void {
    this.meshInstance.setBounds?.(aabb)
  }

  private _configureMaterial(): void {
    this._injectFormatChunks()
    this._material.setDefine('{GSPLAT_INSTANCE_SIZE}', GSPLAT_INSTANCE_SIZE)
    this._material.setDefine('SH_BANDS', '0')
    this._material.setDefine('GSPLAT_SEPARATE_OPACITY', '')
    this._material.setDefine('GSPLAT_WORKBUFFER_DATA', '')
    if (this.adapter.language === 'glsl') {
      this._material.setDefine('GSPLAT_SHADER_EFFECT_VIEW_GUARD', '')
    }
    const colorStream = this.workBuffer.format.getStream('dataColor')
    this._material.setDefine(
      'GSPLAT_COLOR_FLOAT',
      colorStream && colorStream.format !== 'rgba16uint' ? '' : undefined
    )
    this._updateIdDefines()
    this._bindWorkBufferTextures()
    this._material.setParameter('numSplats', 0)
    this.setOrderData()
    this._material.setDefine('DITHER_NONE', '')
    this._material.setDefine('DITHER_BLUENOISE', undefined)
    // Three.js 的 RawShaderMaterial 不会自动注入输出颜色空间转换。冻结的
    // PlayCanvas/旧 WebGL 路径默认保持输入 gamma 颜色，并禁用 tone mapping；
    // Unified GLSL raster 必须显式恢复同一组宏，否则 prepareOutputFromGamma()
    // 会把颜色再次解码到线性空间，导致画面严重暗化。
    if (this.adapter.language === 'glsl') {
      this._material.setDefine('GAMMA_SRGB', '')
      this._material.setDefine('GAMMA_NONE', undefined)
      this._material.setDefine('TONEMAP_NONE', '')
      this._material.setDefine('TONEMAP_LINEAR', undefined)
      this._material.setDefine('TONEMAP_ACES', undefined)
      this._material.setDefine('TONEMAP_FILMIC', undefined)
      this._material.setDefine('TONEMAP_NEUTRAL', undefined)
    }
    this._material.setParameter('renderMode', 0)
    this._material.setParameter('exposure', 1)
    this._material.setParameter('colorBrightness', 0)
    this._material.setParameter('colorTemperature', 0)
    this._material.setParameter('colorContrast', 1)
    this._material.setParameter('colorSaturation', 1)
    this._material.setParameter('colorOpacity', 1)
    this._material.setRasterState({
      cullMode: 'none',
      blendMode: 'premultiplied',
      depthWrite: false,
      depthTest: true,
    })
    this._material.update()
    this.workBufferFormatVersion = this.workBuffer.format.extraStreamsVersion
  }

  private _bindWorkBufferTextures(): void {
    const currentNames = new Set<string>()
    for (const stream of this.workBuffer.format.resourceStreams) {
      const texture = this.workBuffer.getTexture(stream.name)
      if (!texture) continue
      currentNames.add(stream.name)
      this._material.setParameter(stream.name, texture)
    }
    for (const name of this._boundStreamNames) {
      if (!currentNames.has(name)) this._material.deleteParameter(name)
    }
    this._boundStreamNames.clear()
    for (const name of currentNames) this._boundStreamNames.add(name)
  }

  private _injectFormatChunks(): void {
    const chunks = this._material.getShaderChunks(this.adapter.language)
    const declarations = this.workBuffer.format.getInputDeclarations(
      undefined,
      this.adapter.language
    )
    const hasTreeBind = declarations.includes('loadTreeBind')
    chunks.set(
      'gsplatDeclarationsVS',
      `${hasTreeBind ? '#define GSPLAT_HAS_TREE_BIND\n' : ''}${declarations}`
    )
    chunks.set('gsplatReadVS', this.workBuffer.format.getReadCode(this.adapter.language))
    if (this.adapter.language === 'glsl') {
      chunks.set('gsplatWorkBufferVS', gsplatRasterWorkBufferVS)
    }
  }

  private _updateIdDefines(): void {
    const hasPcId = Boolean(this.workBuffer.format.getStream('pcId'))
    this._material.setDefine('GSPLAT_UNIFIED_ID', hasPcId)
    this._material.setDefine('PICK_CUSTOM_ID', hasPcId)
  }

  private _syncWithWorkBufferFormat(): void {
    const nextVersion = this.workBuffer.format.extraStreamsVersion
    if (this.workBufferFormatVersion === nextVersion) return

    this.workBuffer.syncWithFormat()
    this._injectFormatChunks()
    this._bindWorkBufferTextures()
    this._updateIdDefines()
    this._material.update()
    this.workBufferFormatVersion = nextVersion
  }

  private _copyMaterialSettings(sourceMaterial: GSplatMaterialLeaf): void {
    const source = sourceMaterial.captureSnapshot(this.adapter.language)
    const current = this._material.captureSnapshot(this.adapter.language)
    const sourceDefines = new Map(source.defines)
    for (const [name] of current.defines) {
      if (!this._internalDefines.has(name) && !sourceDefines.has(name)) {
        this._material.setDefine(name, undefined)
      }
    }
    for (const [name, value] of source.defines) this._material.setDefine(name, value)
    for (const [name, value] of source.parameters) this._material.setParameter(name, value)

    const sourceChunksKey = createShaderChunksKey(source)
    if (sourceChunksKey !== this._lastSourceChunksKey) {
      const chunks = this._material.getShaderChunks(this.adapter.language)
      chunks.clear()
      for (const [name, value] of source.shaderChunks) chunks.set(name, value)
      this._injectFormatChunks()
      this._lastSourceChunksKey = sourceChunksKey
    }
    this._material.update()
  }

  /**
   * Three 的 RawShaderMaterial 只自动绑定标准矩阵；PlayCanvas GSplat shader 依赖的
   * viewport/camera/RTE uniforms 必须由 renderer 在每个 View 帧显式刷新。
   */
  private _updateFrameUniforms(
    params: GSplatRenderViewParams,
    camera: THREE.Camera & { readonly fov?: number },
    exposure: number
  ): void {
    const width = Math.max(params.width ?? 1, 1)
    const height = Math.max(params.height ?? 1, 1)
    this._viewport.set(width, height)
    this._viewportSize.set(width, height, 1 / width, 1 / height)
    this._material.setParameter('viewport', this._viewport)
    this._material.setParameter('viewport_size', this._viewportSize)

    if (camera instanceof THREE.PerspectiveCamera) {
      this._cameraParams.set(1 / camera.far, camera.far, camera.near, 0)
    } else if (camera instanceof THREE.OrthographicCamera) {
      this._cameraParams.set(1 / camera.far, camera.far, camera.near, 1)
    } else {
      throw new Error('GSplatQuadRenderer 仅支持 PerspectiveCamera 或 OrthographicCamera')
    }
    this._material.setParameter('camera_params', this._cameraParams)
    this._material.setParameter('gsplatProjectionScaleX', camera.projectionMatrix.elements[0])
    this._material.setParameter('minPixelSize', params.minPixelSize)
    this._material.setParameter('exposure', Number.isFinite(exposure) ? exposure : 1)

    camera.getWorldPosition(this._cameraWorldPosition)
    splitHighLow(
      this._cameraWorldPosition.x,
      this._cameraPositionHigh,
      this._cameraPositionLow,
      'x'
    )
    splitHighLow(
      this._cameraWorldPosition.y,
      this._cameraPositionHigh,
      this._cameraPositionLow,
      'y'
    )
    splitHighLow(
      this._cameraWorldPosition.z,
      this._cameraPositionHigh,
      this._cameraPositionLow,
      'z'
    )
    this._material.setParameter('cameraPositionHigh', this._cameraPositionHigh)
    this._material.setParameter('cameraPositionLow', this._cameraPositionLow)

    // WorkBuffer 存储相对 sceneOrigin 的坐标；恢复到相机相对坐标时补回
    // sceneOrigin-cameraPosition。未设置原点时该值自然退化为 -cameraPosition。
    const sceneOrigin = params.sceneOrigin
    splitHighLow(
      (sceneOrigin?.x ?? 0) - this._cameraWorldPosition.x,
      this._originToCameraHigh,
      this._originToCameraLow,
      'x'
    )
    splitHighLow(
      (sceneOrigin?.y ?? 0) - this._cameraWorldPosition.y,
      this._originToCameraHigh,
      this._originToCameraLow,
      'y'
    )
    splitHighLow(
      (sceneOrigin?.z ?? 0) - this._cameraWorldPosition.z,
      this._originToCameraHigh,
      this._originToCameraLow,
      'z'
    )
    this._material.setParameter('originToCameraHigh', this._originToCameraHigh)
    this._material.setParameter('originToCameraLow', this._originToCameraLow)
  }
}

function splitHighLow(
  value: number,
  high: THREE.Vector3,
  low: THREE.Vector3,
  component: 'x' | 'y' | 'z'
): void {
  const highValue = Math.fround(value)
  high[component] = highValue
  low[component] = value - highValue
}

function createShaderChunksKey(snapshot: GSplatMaterialSnapshot): string {
  return snapshot.shaderChunks
    .map(([name, source]) => `${name.length}:${name}:${source.length}:${source}`)
    .join('|')
}
